import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { findGroupByKey, isSectionWriteBlocked } from "../gapsGroupSheets";
import type { GapsHomeModel } from "../useGapsDayModel";
import { closeGapsSheetIfCurrent, type GapsSheetIntent, type GapsSheetIntentOf, type GapsSheetKind } from "../useGapsSheet";
import { GapsPostShiftStep } from "./GapsPostShiftStep";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { isPrefillCandidate } from "./gapsPostShiftPrefill";
import { useSheetOpening } from "./useSheetOpening";

type Props = {
	/**
	 * What the sheet is up for, or `null` when it is not. The store's own intent object: a new object is a new opening
	 * (a fresh, re-prefilled form), the same object is the same opening re-rendering.
	 */
	target: GapsSheetIntentOf<GapsSheetKind.PostShift> | null;
	home: GapsHomeModel;
	/** The day the home is showing — the day the post is for. */
	date: DateTime;
	/** Re-opens the sheet prefilled from a group — a blank post's "short on this day" chips. */
	onPrefillFrom: (groupKey: string) => void;
	/** The Date row's pick: moves this opening, and the home under it, to that day. */
	onMoveDay: (from: GapsSheetIntent, date: DateTime) => void;
};

/**
 * Post a shift, in a sheet over the day's gaps (P3-9) — opened from a group's `+`, the cover prompt after a removal,
 * or the bottom bar's Post. Replaces the PR 2 tap that left the home for `/post-shift/:date`.
 *
 * The group is looked up by key on every render (the narrowed day first, as the home shows it), so the hero's
 * `filled/required` is the home's own; the form's FIELDS are read from it once, when the opening mounts them. Each
 * opening is its own form (keyed by the intent), so a second post never inherits the first one's edits, and the time
 * picker reads its prefill fresh — its `initialTimes` is mount-only by design.
 */
export const GapsPostShiftSheet: FC<Props> = ({ target, home, date, onPrefillFrom, onMoveDay }) => {
	const { t } = useTranslation();
	const { shown, openingKey } = useSheetOpening(target);
	// Closes THIS opening only: a post that finishes after the user moved on must not close what they moved to.
	const onClose = useCallback((): void => {
		if (shown !== null) closeGapsSheetIfCurrent(shown);
	}, [shown]);

	const groupKey = shown === null ? null : shown.groupKey;
	const group = groupKey === null ? undefined : findGroupByKey(home.groups, home.dayGroups, groupKey);
	// A section that cannot say which job it is has nothing a post could be prefilled from (`isSectionWriteBlocked`).
	const shortGroups = useMemo(() => home.groups.filter((candidate) => isPrefillCandidate(candidate) && !isSectionWriteBlocked(candidate)), [home.groups]);

	return (
		// A full page over the home (APP-886), not a drawer.
		<GapsSheetFrame open={target !== null} onClose={onClose} dataTestId="gaps-post-shift-sheet" page={{ label: t("gaps.postSheet.title") }}>
			{shown !== null && openingKey !== null && (
				<GapsPostShiftStep
					key={openingKey}
					group={group}
					shortGroups={shortGroups}
					date={date}
					homeWeekShortfalls={home.isWeekReady ? home.weekShortfalls : undefined}
					onPickDate={(next) => onMoveDay(shown, next)}
					onPrefillFrom={onPrefillFrom}
					onClose={onClose}
				/>
			)}
		</GapsSheetFrame>
	);
};
